import { useQueryClient } from '@tanstack/react-query'
import { ArrowRightIcon, MapPinIcon, PersonStandingIcon, UsersIcon } from 'lucide-react'
import React from 'react'

import { useOnboardingStep } from 'components/hooks/employer_onboarding/useOnboardingStep'
import { Button, ButtonGroup, SolidCircleCheckIcon, SolidCircleXIcon, usePageTitle } from 'slab'
import {
  getGetQuotingProspectsProspectIdQuotesIdGenerationOptionsQueryKey as getQuoteGenerationOptionsQueryKey,
  getGetQuotingProspectsProspectIdQuotesQueryKey as getQuotesQueryKey,
  getQuotingProspectsProspectIdQuotesIdGenerationOptions as getQuoteGenerationOptions,
  usePostQuotingProspectsProspectIdQuotesIdGenerate as useGenerateQuote,
} from 'utils/web_api'

import { useEmployer } from '../EmployerProvider'
import { useQuote } from '../QuoteProvider'
import { SupportLink } from '../SupportLink'
import { useAPIErrorBanner } from '../useErrorBanner'

function ExampleEmployee ({
  age,
  allowance,
  canAfford,
  carrierLogo,
  dependents,
  location,
  image,
  planCost
}: {
  age: React.ReactNode;
  allowance: React.ReactNode;
  canAfford: boolean;
  carrierLogo: string;
  dependents: React.ReactNode;
  location: React.ReactNode;
  image: string;
  planCost: React.ReactNode;
}) {
  return (
    <li role='group'>
      <div>
        <div className='rounded-2xl bg-gray-50 p-4'>
          <div className='flex flex-col items-center'>
            <div className='size-[4.5rem] rounded-full overflow-hidden border-2 border-white shadow'>
              <img
                className='object-cover'
                src={image}
                height={72}
                width={72}
                aria-hidden
              />
            </div>
            <div className='rounded-full bg-white/80 backdrop-blur-sm py-1 px-3 heading-sm -mt-4 border border-gray-200'>
              {allowance}
            </div>
          </div>
          <ul className='mt-3 space-y-1'>
            <li className='flex gap-1 items-center'>
              <MapPinIcon
                aria-label='Location:'
                size={16}
                className='text-gray-500'
              />
              <span className='body-sm'>{location}</span>
            </li>
            <li className='flex gap-1 items-center'>
              <PersonStandingIcon
                aria-label='Age:'
                size={16}
                className='text-gray-500'
              />
              <span className='body-sm'>{age}</span>
            </li>
            <li className='flex gap-1 items-center'>
              <UsersIcon
                aria-label='Dependents:'
                size={16}
                className='text-gray-500'
              />
              <span className='body-sm'>{dependents}</span>
            </li>
          </ul>
        </div>
        <div className='flex flex-row gap-4 justify-between items-center mt-4'>
          <div className='h-10 w-14 shrink-0 bg-white rounded-md border border-gray-200 p-1 flex items-center justify-center'>
            <img
              alt='United Healthcare logo'
              src={carrierLogo}
              className='block object-contain h-full w-full'
            />
          </div>
          <div className='text-right'>
            <div className='body-sm text-gray-500'>
              Plan cost
            </div>
            <div>
              <span className='heading-sm'>{planCost}</span>
              <span className='body-sm'>/mo</span>
            </div>
          </div>
        </div>
        <div className='mt-4'>
          {canAfford
            ? (
              <div className='rounded-md bg-green-50 text-green-500 px-3 py-1 body-sm-bold flex items-center justify-center gap-1'>
                <SolidCircleCheckIcon size={12} className='text-green-400' />
                <span>Can afford</span>
              </div>
              )
            : (
              <div className='rounded-md bg-red-50 text-red-500 px-3 py-1 body-sm-bold flex items-center justify-center gap-1'>
                <SolidCircleXIcon size={12} className='text-red-400' />
                <span>Can't afford</span>
              </div>
              )}
        </div>
      </div>
    </li>
  )
}

interface AllowanceStrategyPageProps {
  assets?: {
    images?: Record<string, string>;
  };
}

export function AllowanceStrategyPage ({ assets }: AllowanceStrategyPageProps) {
  usePageTitle('How allowances work')

  const { employer, loading: loadingEmployer } = useEmployer()
  const { quote, loading: loadingQuote } = useQuote()
  const queryClient = useQueryClient()
  const isFetching = loadingEmployer || loadingQuote
  const { navigateToPreviousStep, navigateToNextStep } = useOnboardingStep()

  const { errorBanner, setError } = useAPIErrorBanner({
    fallbackMessage: 'Could not generate quote because of an error',
  })

  const generateQuoteMutation = useGenerateQuote()

  async function handleSubmit () {
    try {
      // Perhaps in the future, the `generateQuoteMutation` endpoint should be
      // updated to get the "quote generation options" on the BE instead of
      // requiring the FE to first fetch them and then pass them to the BE.
      const generationOptions = await queryClient.fetchQuery({
        queryKey: getQuoteGenerationOptionsQueryKey(employer!.quoting_prospect_id!, quote!.id),
        queryFn: () => getQuoteGenerationOptions(employer!.quoting_prospect_id!, quote!.id)
      })

      await generateQuoteMutation.mutateAsync({
        prospectId: employer!.quoting_prospect_id!,
        id: quote!.id,
        data: {
          coverage_percentiles: generationOptions.coverage_percentiles,
          coverage_start_date: generationOptions.coverage_start_date,
          metal_tiers: generationOptions.metal_tiers,
          year: generationOptions.quote_year,
        }
      })

      await queryClient.invalidateQueries({
        queryKey: getQuotesQueryKey(employer!.quoting_prospect_id!, undefined)
      })

      navigateToNextStep()
    } catch (error) {
      // TODO: Consider a different error handling strategy. If generating the
      // quote fails, it's likely because of data collected on a previous page.
      // Maybe we should direct the user to the relevant page?
      setError(error)
    }
  }

  return (
    <div className='pt-6 xs:pt-10 xs:px-6 flex flex-col grow'>
      <div className='max-w-[80rem] mx-auto'>
        <h1 className='heading-lg xs:heading-xl mb-16 text-balance text-center'>
          There are two ways you can give your employees their allowance:
        </h1>
        <div className='flex flex-col lg:flex-row gap-6 items-stretch mb-12'>
          <div className='p-6 bg-white rounded-2xl border border-gray-200 shadow flex-1'>
            <h2 className='heading-sm text-center mb-2'>
              Same amount for everyone
            </h2>
            <p className='body text-center'>
              Specify an exact allowance, nationwide or by state. All employees in
              the specified area will receive the same monthly allowance.
            </p>
            <ul className='grid xs:grid-cols-3 gap-10 xs:gap-6 mt-10'>
              <ExampleEmployee
                age='26 years old'
                allowance='$900'
                carrierLogo={assets?.images?.['uhc-logo'] ?? ''}
                dependents='No dependents'
                location='San Francisco, CA'
                image={assets?.images?.['employee-1-avatar'] ?? ''}
                planCost='$780'
                canAfford
              />
              <ExampleEmployee
                age='33 years old'
                allowance='$900'
                carrierLogo={assets?.images?.['uhc-logo'] ?? ''}
                dependents='2 dependents'
                location='Atlanta, GA'
                image={assets?.images?.['employee-2-avatar'] ?? ''}
                planCost='$1,300'
                canAfford={false}
              />
              <ExampleEmployee
                age='40 years old'
                allowance='$900'
                carrierLogo={assets?.images?.['uhc-logo'] ?? ''}
                dependents='No dependents'
                location='Miami, FL'
                image={assets?.images?.['employee-3-avatar'] ?? ''}
                planCost='$960'
                canAfford={false}
              />
            </ul>
          </div>
          <div className='p-6 bg-white rounded-2xl border border-gray-200 shadow flex-1'>
            <h2 className='heading-sm text-center mb-2 bg-[linear-gradient(258deg,#FF5900_24.58%,#B700FF_50.19%,#4000FF_77.16%,#00BFFF_100%)] text-transparent bg-clip-text'>
              Smart allowances
            </h2>
            <p className='body text-center'>
              Each employee gets a dollar amount that is calculated based on
              their unique circumstances.
            </p>
            <ul className='grid xs:grid-cols-3 gap-10 xs:gap-6 xs:mt-10'>
              <ExampleEmployee
                age='26 years old'
                allowance='$875'
                carrierLogo={assets?.images?.['uhc-logo'] ?? ''}
                dependents='No dependents'
                location='San Francisco, CA'
                image={assets?.images?.['employee-1-avatar'] ?? ''}
                planCost='$780'
                canAfford
              />
              <ExampleEmployee
                age='33 years old'
                allowance='$1,300'
                carrierLogo={assets?.images?.['uhc-logo'] ?? ''}
                dependents='2 dependents'
                location='Atlanta, GA'
                image={assets?.images?.['employee-2-avatar'] ?? ''}
                planCost='$1,300'
                canAfford
              />
              <ExampleEmployee
                age='40 years old'
                allowance='$1,000'
                carrierLogo={assets?.images?.['uhc-logo'] ?? ''}
                dependents='No dependents'
                location='Miami, FL'
                image={assets?.images?.['employee-3-avatar'] ?? ''}
                planCost='$960'
                canAfford
              />
            </ul>
          </div>
        </div>
        {errorBanner && (
          <div className='mb-12'>
            {errorBanner}
          </div>
        )}
        <ButtonGroup size='large' align='center'>
          <Button
            variant='tertiary-deemphasized'
            onClick={navigateToPreviousStep}
          >
            Back
          </Button>
          <Button
            variant='primary'
            disabled={!!isFetching || generateQuoteMutation.isPending}
            onClick={() => {
              handleSubmit()
            }}
            rightIcon={ArrowRightIcon}
          >
            Customize allowances
          </Button>
        </ButtonGroup>
      </div>
      <div className='mt-auto pt-10 text-center'>
        <SupportLink />
      </div>
    </div>
  )
}
